import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User } from '@/api/entities';
import { supabase } from '@/lib/supabase';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, RefreshCw, ExternalLink, ChevronRight, X, MessageSquare, List, DollarSign, ArrowLeftRight, Calendar, Gift } from 'lucide-react';
import { getCityName } from '@/components/airportUtils';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { calculateReviewPagePricing } from '@/utils';
import { AdminAllTripsModal } from '@/components/reserve';
import AdminAllZeroTripsModal from '@/components/defera-zero/AdminAllZeroTripsModal';
import { useInstallmentsCopy } from '@/hooks/useInstallmentsCopy';
import { useInstallmentsCheckoutProvider } from '@/hooks/useInstallmentsCheckoutProvider';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import { resolveCarrierLogo, resolveAirlineIata } from '@/utils/airlineBranding';
import { seatPurchaseMarginContribution } from '@/utils/seatPurchaseEconomics';
import { insurancePremiumCents, withoutInsurancePremium } from '../../supabase/functions/_shared/trip-insurance.js';
import { MILES_LIABILITY_CENTS_PER_MILE, readCheckoutMiles } from '../../supabase/functions/_shared/miles-redemption.js';

const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  if (num % 1 === 0) {
    return num.toLocaleString('en-US');
  }
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const extractRoute = (flightData) => {
  const slices = flightData?.slices || [];
  if (slices.length === 0) return 'N/A';
  
  const firstSlice = slices[0];
  const firstSegments = firstSlice?.segments || [];
  const firstSegment = firstSegments[0];
  
  const originCode = firstSegment?.origin?.iata_code || 
                     firstSlice?.origin?.iata_code ||
                     firstSlice?.origin?.id ||
                     'N/A';
  
  let destinationCode = 'N/A';
  
  if (slices.length === 1) {
    const lastSegment = firstSegments[firstSegments.length - 1];
    destinationCode = lastSegment?.destination?.iata_code ||
                      firstSlice?.destination?.iata_code ||
                      firstSlice?.destination?.id ||
                      'N/A';
  } else {
    const lastSliceOfOutbound = slices[0];
    const lastSegmentsOfOutbound = lastSliceOfOutbound?.segments || [];
    const lastSegmentOfOutbound = lastSegmentsOfOutbound[lastSegmentsOfOutbound.length - 1];
    destinationCode = lastSegmentOfOutbound?.destination?.iata_code ||
                      lastSliceOfOutbound?.destination?.iata_code ||
                      lastSliceOfOutbound?.destination?.id ||
                      'N/A';
  }
  
  if (originCode === destinationCode || originCode === 'N/A' || destinationCode === 'N/A') {
    const departureCity = flightData?.departure_city || flightData?._searchParams?.origin;
    const arrivalCity = flightData?.arrival_city || flightData?._searchParams?.destination;
    if (departureCity && arrivalCity && departureCity !== arrivalCity) {
      return `${departureCity} → ${arrivalCity}`;
    }
  }
  
  return `${originCode} → ${destinationCode}`;
};

// Extract destination city name for SMS messages
const extractDestinationCity = (flightData) => {
  const slices = flightData?.slices || [];
  if (slices.length === 0) return null;
  
  const firstSlice = slices[0];
  const firstSegments = firstSlice?.segments || [];
  const lastSegment = firstSegments[firstSegments.length - 1];
  
  // Try to get city name directly from destination data
  const directCityName = lastSegment?.destination?.city_name ||
                         lastSegment?.destination?.city ||
                         firstSlice?.destination?.city_name ||
                         firstSlice?.destination?.city;
  
  if (directCityName) return directCityName;
  
  // Get IATA code and convert to city name using the utility
  const iataCode = lastSegment?.destination?.iata_code ||
                   firstSlice?.destination?.iata_code ||
                   flightData?.arrival_city ||
                   flightData?._searchParams?.destination;
  
  if (iataCode) {
    // Use getCityName utility to convert airport code to city name
    return getCityName(iataCode);
  }
  
  return null;
};

// Check if booking has a return flight
const hasReturnFlight = (flightData) => {
  const slices = flightData?.slices || [];
  return slices.length > 1;
};

// Compose SMS message for T1 (outbound) or T2 (return)
const composeSmsMessage = (booking, messageType) => {
  const lastName = booking.userName?.split(' ').pop() || 'Traveler';
  // Default to Ms. for title - could be enhanced to use passenger title if available
  const title = booking.passengerTitle || 'Ms.';
  
  // Convert destination city - if it's an airport code (3 letters), convert it
  let destinationCity = booking.destinationCity || 'your destination';
  if (destinationCity && destinationCity.length === 3 && destinationCity === destinationCity.toUpperCase()) {
    // Looks like an airport code, convert it
    destinationCity = getCityName(destinationCity);
  }
  
  if (messageType === 'T1') {
    return `Hi ${title} ${lastName}, this is Josh from Defera. Hope you had a good flight to ${destinationCity} today. If you need anything during your trip, let us know.`;
  } else {
    return `Hello again ${title} ${lastName}, this is Josh from Defera. How was your flight back yesterday?`;
  }
};

// Open SMS compose with message pre-filled (OpenPhone deep link with text= for prefill)
const openSmsCompose = (phoneNumber, message, bookingRef) => {
  if (!phoneNumber) {
    toast.error(`No phone number on file for booking ${bookingRef || 'unknown'}`);
    return false;
  }
  
  // Clean phone number (remove spaces, dashes, etc.) and ensure it starts with +
  let cleanPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
  if (!cleanPhone.startsWith('+')) {
    // If no country code, assume US (+1)
    if (cleanPhone.length === 10) {
      cleanPhone = '+1' + cleanPhone;
    } else {
      cleanPhone = '+' + cleanPhone;
    }
  }
  
  // Open OpenPhone with number and pre-filled message (same prefill format as elsewhere)
  const params = new URLSearchParams({
    number: cleanPhone,
    text: message
  });
  const openPhoneUrl = `openphone://message?${params.toString()}`;
  
  const link = document.createElement('a');
  link.href = openPhoneUrl;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
  }, 100);
  
  toast.success('Opening conversation with message pre-filled.');
  return true;
};

const getStatusBadge = (status) => {
  const statusConfig = {
    'pending': { bg: 'bg-brand-500/20', text: 'text-brand-400', label: 'Pending' },
    'confirmed': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Confirmed' },
    'cancelled': { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Cancelled' },
    'completed': { bg: 'bg-sky-400/20', text: 'text-sky-300', label: 'Completed' },
    'defaulted': { bg: 'bg-orange-600/20', text: 'text-orange-400', label: 'Defaulted' },
    'refunded': { bg: 'bg-brand-500/20', text: 'text-brand-400', label: 'Refunded' },
    'in_progress': { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'In Progress' },
    'ready_for_push': { bg: 'bg-cyan-500/20', text: 'text-cyan-400', label: 'Ready' }
  };
  const config = statusConfig[status] || { bg: 'bg-gray-500/20', text: 'text-gray-400', label: status || 'Unknown' };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const getRowAlertClass = (booking) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const repaymentDate = booking.finalRepaymentDate ? new Date(booking.finalRepaymentDate) : null;
  if (repaymentDate) repaymentDate.setHours(0, 0, 0, 0);
  
  const isRepaymentDueOrPast = repaymentDate && repaymentDate <= today;
  const isConfirmed = booking.status === 'confirmed';
  const isNotReleased = !booking.isDisbursed;
  const isNotBooked = !booking.isTicketPurchased;
  const isNotPaid = !booking.isReimbursed;
  
  // RED: Final repayment is today or past, AND (status is Confirmed OR not Released)
  if (isRepaymentDueOrPast && (isConfirmed || isNotReleased)) {
    return 'bg-red-900/30 hover:bg-red-900/40';
  }
  
  // YELLOW: Status is Confirmed but not Booked
  if (isConfirmed && isNotBooked) {
    return 'bg-brand-700/40 hover:bg-brand-700/50';
  }
  
  // ROYAL BLUE: Booked but not Paid
  if (booking.isTicketPurchased && isNotPaid) {
    return 'bg-brand-700/60 hover:bg-brand-700/70';
  }
  
  return '';
};

/**
 * One booking as the Accounting table reads it, from an /api/admin/accounting
 * row (DB columns + the server's `_accounting_computed`).
 *
 * A Faye travel insurance premium rides inside the stored totals (migration
 * 156) but is collected for Faye, so every figure is derived from the TRAVEL
 * row: base fare, fees, grand total, miles, investor and backer cost, profit.
 * The premium travels alongside as `insurancePremium` (dollars, 0 without one).
 * A booking without a premium is processed exactly as before.
 */
export function buildAccountingRow(rawBooking, { installmentsCheckoutProvider } = {}) {
  const booking = withoutInsurancePremium(rawBooking);
  const insurancePremium = insurancePremiumCents(rawBooking) / 100;
  const flightData = booking.flight_data || {};
  const accounting = flightData._accounting || {};
  const finance = flightData._finance || {};
  const computed = booking._accounting_computed || {};
  const slices = flightData.slices || [];
  // Raw values from API (Supabase returns snake_case: total_amount, service_fee, etc.)
  const rawTotalAmount = parseFloat(booking.total_amount ?? booking.totalAmount) || 0;
  const rawServiceFee = parseFloat(booking.service_fee ?? booking.serviceFee) || 0;
  const rawHoldFee = parseFloat(booking.hold_fee ?? booking.holdFee) || 0;
  const rawGrandTotal = parseFloat(booking.grand_total ?? booking.grandTotal ?? booking.grand_total_with_hold ?? booking.grandTotalWithHold) || 0;
  
  const route = extractRoute(flightData);
  
  // Extract airline logo from first segment (same name-first resolution
  // as FlightListItem — admin renames re-brand the logo too)
  const firstSegment = slices[0]?.segments?.[0];
  const marketingCarrier = firstSegment?.marketing_carrier || firstSegment?.operating_carrier || {};
  const airlineLogo = resolveCarrierLogo(marketingCarrier);
  const airlineCode = resolveAirlineIata(marketingCarrier.name) || marketingCarrier.iata_code || flightData.marketing_airline || '';
  
  // Parse departure date properly (date only, no time)
  const departingAt = firstSegment?.departing_at;
  const departureDate = departingAt 
    ? new Date(departingAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'N/A';
  
  // Check if departure date is in the future (for T1/T2 enable/disable logic)
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to midnight for date-only comparison
  const departureDateObj = departingAt ? new Date(departingAt) : null;
  if (departureDateObj) {
    departureDateObj.setHours(0, 0, 0, 0); // Reset time to midnight
  }
  const isDepartureInFuture = departureDateObj ? departureDateObj > today : false;
  
  // CRITICAL: For old bookings, use stored amounts in correct priority order
  const pricingSnapshot = flightData.pricing_snapshot;
  
  // Resolve payment method (may be in flight_data or pricing_snapshot from cart push)
  const paymentMethod = flightData._payment_method || flightData.payment_method || booking.payment_method || flightData.booking_option || pricingSnapshot?.payment_method || 'installments';
  const isMilesOnly = paymentMethod === 'miles';
  const isMilesInstallments = paymentMethod === 'miles_installments';
  const milesToDeplete = flightData._miles_to_deplete ?? flightData.miles_to_deplete ?? 0;
  
  // Miles+Installments: use pricing_snapshot when it has cash_portion/miles_used
  let snapshotCashPortion = parseFloat(pricingSnapshot?.cash_portion ?? 0);
  let snapshotMilesUsed = parseFloat(pricingSnapshot?.miles_used ?? 0);
  const creditsTotal = parseFloat(flightData.credits_total || 0);
  const hasMilesSnapshot = isMilesInstallments && pricingSnapshot && (snapshotMilesUsed > 0 || snapshotCashPortion >= 0);
  const hasStayData = !!(booking.stay_data || (booking.stays && booking.stays.length > 0) || flightData.stay_data);
  const isFlightHotelPackage = !!(flightData._is_flight_hotel_package || hasStayData);
  
  // CRITICAL: Check booking table first (database record), then flight_data, then pricing_snapshot
  const storedServiceFee = parseFloat(
    booking.service_fee ??           // Database record - most authoritative
    booking.serviceFee ??            // CamelCase fallback (API/ORM)
    flightData.service_fee ??        // Flight data embedded field
    flightData.serviceFee ??         // CamelCase fallback
    pricingSnapshot?.service_fee ??  // Pricing snapshot from booking time
    pricingSnapshot?.serviceFee ??   // CamelCase fallback
    0
  );
  
  // Get initial base fare from available sources
  let baseFare = parseFloat(
    pricingSnapshot?.original_base_fare ?? 
    pricingSnapshot?.originalBaseFare ??
    booking.total_amount ??  // CRITICAL: For old bookings, total_amount IS the base fare
    flightData.base_amount ?? 
    flightData.baseAmount ??
    flightData.total_amount ?? 
    flightData.totalAmount ?? 
    0
  );
  
  let serviceFee, serviceFeeRate, totalWithFee, holdFee, grandTotal, storedHoldFee;
  
  if (hasMilesSnapshot) {
    // Miles+Installments: use pricing_snapshot (matches Carts/EditCart)
    baseFare = parseFloat(pricingSnapshot.original_base_fare || baseFare);
    serviceFee = parseFloat(pricingSnapshot.service_fee || 0);
    holdFee = parseFloat(pricingSnapshot.hold_fee || 0);
    grandTotal = parseFloat(pricingSnapshot.grand_total || 0);
    totalWithFee = snapshotCashPortion + serviceFee;
    serviceFeeRate = snapshotCashPortion > 0 ? serviceFee / snapshotCashPortion : 0;
    storedHoldFee = holdFee;
  } else if (paymentMethod === 'reserve') {
    serviceFee = baseFare * 0.11;
    serviceFeeRate = 0.11;
    holdFee = 0;
    storedHoldFee = 0;
    totalWithFee = baseFare + serviceFee;
    grandTotal = totalWithFee;
  } else if (paymentMethod === 'defera_zero') {
    // Defera Zero: booking.total_amount/grand_total is the FULL total (base + service + hold)
    // Derive base fare: base = grand_total - service_fee - hold_fee
    grandTotal = rawGrandTotal || rawTotalAmount;
    serviceFee = rawServiceFee;
    holdFee = rawHoldFee;
    storedHoldFee = holdFee;
    baseFare = Math.max(0, grandTotal - serviceFee - holdFee);
    totalWithFee = baseFare + serviceFee;
    serviceFeeRate = baseFare > 0 ? serviceFee / baseFare : 0.11;
  } else if (isMilesOnly) {
    // Miles-only: no fees
    serviceFee = 0;
    serviceFeeRate = 0;
    holdFee = 0;
    storedHoldFee = 0;
    totalWithFee = 0;
    grandTotal = baseFare;
  } else if (isMilesInstallments && milesToDeplete > 0) {
    // Fallback: compute when snapshot missing (e.g. booking pushed before snapshot fix)
    const baseAfterCredits = Math.max(0, baseFare - creditsTotal);
    const pricing = calculateReviewPagePricing(
      baseAfterCredits,
      0,
      'miles_installments',
      milesToDeplete,
      0,
      isFlightHotelPackage,
      installmentsCheckoutProvider
    );
    serviceFee = pricing.serviceFee;
    holdFee = pricing.holdFee;
    totalWithFee = pricing.subtotalBeforeHold;
    grandTotal = pricing.totalExcludingSeats;
    storedHoldFee = holdFee;
    serviceFeeRate = pricing.cashPortion > 0 ? serviceFee / pricing.cashPortion : 0;
    snapshotCashPortion = pricing.cashPortion;
    snapshotMilesUsed = pricing.milesUsed;
  } else {
  
  // CRITICAL FIX: For old bookings with stored service fee, validate and correct base fare
  // If we have a stored service fee, work backwards to find the correct base fare
  // This handles cases where booking.total_amount might have been set incorrectly
  if (storedServiceFee > 0 && baseFare > 0) {
    // Calculate what the service fee rate would be with current base
    const calculatedRate = baseFare > 0 ? storedServiceFee / baseFare : 0;
    
    // Check if calculated rate matches any known tiered structure (current or legacy)
    // Current: 30%, 10%, 8% | Legacy current-tier: 40%, 10%, 8% | Previous: 18%, 12% | Legacy: 14%, 16%
    // Allow small rounding tolerance (0.001 = 0.1%)
    const isRateValid = (
      Math.abs(calculatedRate - 0.3) < 0.001 ||
      Math.abs(calculatedRate - 0.4) < 0.001 ||
      Math.abs(calculatedRate - 0.1) < 0.001 ||
      Math.abs(calculatedRate - 0.18) < 0.001 ||
      Math.abs(calculatedRate - 0.16) < 0.001 ||  // Legacy rate
      Math.abs(calculatedRate - 0.14) < 0.001 ||  // Legacy rate
      Math.abs(calculatedRate - 0.12) < 0.001 ||
      Math.abs(calculatedRate - 0.08) < 0.001
    );

    // If rate doesn't match tiered structure, recalculate base fare from stored service fee
    if (!isRateValid) {
      // Work backwards from stored service fee to find correct base fare
      // Try each tier (current and legacy) and see which produces a base fare in the correct range
      const baseAt30 = storedServiceFee / 0.3;
      const baseAt40 = storedServiceFee / 0.4;
      const baseAt10 = storedServiceFee / 0.1;
      const baseAt18 = storedServiceFee / 0.18;
      const baseAt16 = storedServiceFee / 0.16;  // Legacy rate
      const baseAt14 = storedServiceFee / 0.14;  // Legacy rate
      const baseAt12 = storedServiceFee / 0.12;
      const baseAt8 = storedServiceFee / 0.08;
      
      // Determine which tier the service fee belongs to based on base fare ranges
      // Check current tiers first (30%, 10%, 8%), then legacy 40% under $200, then previous (18%, 12%), then legacy
      if (baseAt30 > 0 && baseAt30 < 200) {
        // Service fee suggests base < $200 (30% tier - current)
        baseFare = Math.round(baseAt30 * 100) / 100;
      } else if (baseAt40 > 0 && baseAt40 < 200) {
        // Service fee suggests base < $200 (40% tier - legacy)
        baseFare = Math.round(baseAt40 * 100) / 100;
      } else if (baseAt18 > 0 && baseAt18 < 200) {
        // Service fee suggests base < $200 (18% tier - previous)
        baseFare = Math.round(baseAt18 * 100) / 100;
      } else if (baseAt14 > 0 && baseAt14 < 200) {
        // Legacy: Service fee suggests base < $200 (14% tier)
        baseFare = Math.round(baseAt14 * 100) / 100;
      } else if (baseAt10 >= 200 && baseAt10 < 700) {
        // Service fee suggests base $200-$699 (10% tier - current)
        baseFare = Math.round(baseAt10 * 100) / 100;
      } else if (baseAt12 >= 200 && baseAt12 < 700) {
        // Service fee suggests base $200-$699 (12% tier - previous)
        baseFare = Math.round(baseAt12 * 100) / 100;
      } else if (baseAt16 >= 200 && baseAt16 < 700) {
        // Legacy: Service fee suggests base $200-$699 (16% tier)
        baseFare = Math.round(baseAt16 * 100) / 100;
      } else if (baseAt8 >= 700) {
        // Service fee suggests base >= $700 (8% tier)
        baseFare = Math.round(baseAt8 * 100) / 100;
      } else if (baseAt12 >= 700) {
        // Legacy: Service fee suggests base >= $700 (12% tier)
        baseFare = Math.round(baseAt12 * 100) / 100;
      }
      // If still no match (edge cases), use the tier that produces base closest to original
      if (baseFare === parseFloat(pricingSnapshot?.original_base_fare || booking.total_amount || flightData.base_amount || flightData.total_amount || 0)) {
        const originalBase = baseFare;
        const candidates = [
          { base: baseAt30, tier: 0.3 },
          { base: baseAt40, tier: 0.4 },
          { base: baseAt10, tier: 0.1 },
          { base: baseAt18, tier: 0.18 },
          { base: baseAt16, tier: 0.16 },  // Legacy
          { base: baseAt14, tier: 0.14 },  // Legacy
          { base: baseAt12, tier: 0.12 },
          { base: baseAt8, tier: 0.08 }
        ].filter(c => c.base > 0 && c.base < 50000); // Reasonable bounds
        
        if (candidates.length > 0) {
          // Find candidate that produces service fee closest to stored value
          const bestMatch = candidates.reduce((best, candidate) => {
            const candidateServiceFee = candidate.base * candidate.tier;
            const bestServiceFee = best.base * best.tier;
            const candidateDiff = Math.abs(candidateServiceFee - storedServiceFee);
            const bestDiff = Math.abs(bestServiceFee - storedServiceFee);
            return candidateDiff < bestDiff ? candidate : best;
          });
          baseFare = Math.round(bestMatch.base * 100) / 100;
        }
      }
    }
  }
  
  let serviceFee, serviceFeeRate;
  
  if (isFlightHotelPackage) {
    // Flight+hotel: service fee is WAIVED
    serviceFee = 0;
    serviceFeeRate = 0;
  } else if (storedServiceFee > 0 && baseFare > 0) {
    // Existing booking: use stored service fee
    serviceFee = storedServiceFee;
    serviceFeeRate = baseFare > 0 ? serviceFee / baseFare : 0;
  } else {
    // FALLBACK: For bookings without stored service_fee, use LEGACY rates based on booking date
    // Old bookings (before rate change) used: 14% under $200, 16% $200-$699, 12% $700+
    // This ensures old bookings display correctly even if service_fee wasn't stored
    // We default to legacy rates since most bookings in the system are pre-rate-change
    serviceFeeRate = baseFare < 200 ? 0.14 : (baseFare < 700 ? 0.16 : 0.12);
    serviceFee = baseFare * serviceFeeRate;
  }
  const totalWithFee = baseFare + serviceFee;
  
  // CRITICAL: Use stored hold_fee if available (existing bookings), otherwise calculate for new bookings
  storedHoldFee = parseFloat(booking.hold_fee || flightData.hold_fee || pricingSnapshot?.hold_fee || 0);
  
  if (storedHoldFee > 0) {
    // Existing booking: use stored hold fee
    holdFee = storedHoldFee;
    grandTotal = parseFloat(booking.grand_total || flightData.grand_total || pricingSnapshot?.grand_total || 0) || (totalWithFee + holdFee);
  } else {
    // FALLBACK: For bookings without stored hold_fee, use LEGACY rate (16%)
    // Old bookings used 16% hold fee, new bookings use 15%
    // Default to legacy since most bookings in system are pre-rate-change
    holdFee = totalWithFee * 0.16;
    grandTotal = totalWithFee + holdFee;
  }
  }
  
  // Commission removed: no longer earned (ended when hold fee structure changed)
  
  // Read investor cost from backend-calculated _finance fields
  // Reserve: investor still gets 14% of service fee; Stripe fees are additional expense
  const isReserve = paymentMethod === 'reserve';
  const investorCost = (
    finance.investor_cost !== undefined 
      ? parseFloat(finance.investor_cost) 
      : serviceFee * 0.14
  );
  // Reserve Stripe fees: 2.9% + $0.30 on the booking debit + same on the top-up(s) that funded it
  // Conservative estimate: 2 transactions (one top-up, one booking debit) per trip
  const reserveStripeFees = isReserve ? (grandTotal * 0.029 + 0.30) * 2 : 0;
  
  // Mileage liability: each Mile earned at its trip value (MILES_LIABILITY_CENTS_PER_MILE, 1.25¢)
  // Miles earned formula: 2 miles per $1 spent on grandTotalWithHold
  // Priority: flight_data.miles_earned > calculate from grandTotalWithHold
  const grandTotalWithHold = parseFloat(booking.grand_total_with_hold || 0) || grandTotal;
  
  // Only exclude miles-only (no miles earned). Reserve earns miles like installments.
  const milesEarned = parseFloat(flightData.miles_earned || 0) || 
    (isMilesOnly ? 0 : Math.floor(grandTotalWithHold * 2));
  
  const mileageLiability = finance.mileage_liability !== undefined
    ? parseFloat(finance.mileage_liability)
    : (milesEarned * MILES_LIABILITY_CENTS_PER_MILE) / 100;
  
  // Profit = service fee only (commission was removed)
  const profit = serviceFee;
  // Backer fee is 25% of service fee if booking has a backer
  const backerId = booking.backer_id || booking.backerId || null;
  const backerFee = backerId ? (serviceFee * 0.25) : 0;
  
  // Net profit = profit - costs (investor + mileage + backer + stripe fees for reserve)
  const netProfit = profit - investorCost - mileageLiability - backerFee - reserveStripeFees;
  
  const firstName = booking.user_first_name || '';
  const lastName = booking.user_last_name || '';
  // Fallback: use first passenger name when user profile/email is missing
  const dbPassengersForName = booking.passengers || [];
  const fdPassengersForName = flightData.passengers || [];
  const firstPassenger = [...dbPassengersForName, ...fdPassengersForName][0];
  const firstPassengerName = firstPassenger
    ? `${(firstPassenger.given_name || firstPassenger.first_name || '').trim()} ${(firstPassenger.family_name || firstPassenger.last_name || '').trim()}`.trim()
    : '';
  const displayName = (firstName && lastName)
    ? `${firstName} ${lastName}`.trim()
    : (firstName || lastName || booking.user_email?.split('@')[0] || firstPassengerName || 'Unknown');

  // Format purchase date same as departure date
  const createdAt = booking.created_at;
  const purchaseDate = createdAt 
    ? new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'N/A';
  
  // Find matching passenger (same name as user who booked) for phone number
  // Check multiple sources: passengers table, flight_data.passengers, and flight_data embedded data
  const dbPassengers = booking.passengers || [];
  const flightDataPassengers = flightData.passengers || [];
  const allPassengers = [...dbPassengers, ...flightDataPassengers];
  
  // Helper to find matching passenger by name
  const findMatchingPassenger = (passengerList) => {
    return passengerList.find(p => {
      const pFirstName = (p.given_name || p.first_name || p.givenName || p.firstName || '').toLowerCase().trim();
      const pLastName = (p.family_name || p.last_name || p.familyName || p.lastName || '').toLowerCase().trim();
      const userFirstName = firstName.toLowerCase().trim();
      const userLastName = lastName.toLowerCase().trim();
      return pFirstName === userFirstName && pLastName === userLastName;
    });
  };
  
  // Try to find matching passenger, fallback to first passenger with phone
  let matchingPassenger = findMatchingPassenger(allPassengers);
  if (!matchingPassenger) {
    // No name match, try to find any passenger with a phone number
    matchingPassenger = allPassengers.find(p => 
      p.phone_number || p.phone || p.phoneNumber
    ) || allPassengers[0];
  }
  
  // Extract phone from multiple possible field names
  const passengerPhone = matchingPassenger?.phone_number || 
                         matchingPassenger?.phone || 
                         matchingPassenger?.phoneNumber || 
                         '';
  const passengerTitle = matchingPassenger?.title || '';
  
  // Extract destination city and check for return flight
  const destinationCity = extractDestinationCity(flightData);
  const hasReturn = hasReturnFlight(flightData);
  
  // Get T1/T2 checked state from accounting data
  const t1Checked = accounting.t1_checked || false;
  const t2Checked = accounting.t2_checked || false;
  
  // Defera Miles. A trip booked with Miles alone stores no cash (every money
  // column is 0); what it sold for is in pricing_snapshot
  // (server/miles-only-booking.js). Read from the columns it was a $0 fare,
  // so once its actual cost was entered the whole airfare showed as a loss.
  // A trip Miles paid for in part stores the plan's smaller total; the Miles'
  // value (flight_data.miles_discount_cents) is the rest of its price. Either
  // way those Miles were already booked as a cost when they were earned (the
  // mileage liability on the trip that earned them), so they count toward
  // what this trip sold for: an overage for the airfare they paid would book
  // the same Miles a second time.
  const milesOnlyPrice = isMilesOnly && pricingSnapshot?.paid_with_miles ? {
    total: (Number(pricingSnapshot.trip_total_cents) || 0) / 100,
    serviceFee: (Number(pricingSnapshot.service_fee_cents) || 0) / 100,
    holdFee: (Number(pricingSnapshot.hold_fee_cents) || 0) / 100,
  } : null;
  const milesClaim = milesOnlyPrice ? null : readCheckoutMiles(flightData);
  const milesPaidValue = milesClaim?.valid ? milesClaim.discountCents / 100 : 0;

  // Prefer: 1) raw API fields (from Supabase), 2) server _accounting_computed, 3) client calculation
  // Defera Zero: total_amount is grand total, not base fare - use our derived baseFare
  const serviceFeeDerived = rawServiceFee > 0 ? rawServiceFee : (rawGrandTotal > rawTotalAmount ? Math.round((rawGrandTotal - rawTotalAmount - rawHoldFee) * 100) / 100 : 0);
  const baseFareFinal = milesOnlyPrice
    ? Math.max(0, milesOnlyPrice.total - milesOnlyPrice.serviceFee - milesOnlyPrice.holdFee)
    : (paymentMethod === 'defera_zero') ? baseFare : (rawTotalAmount > 0 ? rawTotalAmount : (computed.baseFare ?? baseFare));
  const serviceFeeFinal = milesOnlyPrice
    ? milesOnlyPrice.serviceFee
    : (rawServiceFee > 0 || serviceFeeDerived > 0) ? (rawServiceFee || serviceFeeDerived) : (computed.serviceFee ?? ((typeof serviceFee === 'number' && Number.isFinite(serviceFee)) ? serviceFee : 0));
  const totalWithFeeFinal = baseFareFinal + serviceFeeFinal;
  const holdFeeFinal = milesOnlyPrice ? milesOnlyPrice.holdFee : (rawHoldFee > 0 ? rawHoldFee : (computed.holdFee ?? holdFee));
  const grandTotalFinal = milesOnlyPrice ? milesOnlyPrice.total : (rawGrandTotal > 0 ? rawGrandTotal : (computed.grandTotal ?? grandTotal));
  const investorCostFinal = serviceFeeFinal * 0.14;
  const backerFeeFinal = backerId ? (serviceFeeFinal * 0.25) : (computed.backerFee ?? backerFee);
  const profitFinal = serviceFeeFinal;
  const mileageLiabilityFinal = isMilesOnly ? 0 : (grandTotalFinal > 0 ? (Math.floor(grandTotalFinal * 2) * MILES_LIABILITY_CENTS_PER_MILE) / 100 : 0);
  const reserveStripeFeesFinal = isReserve ? (grandTotalFinal * 0.029 + 0.30) * 2 : 0;
  const netProfitFinal = profitFinal - investorCostFinal - mileageLiabilityFinal - backerFeeFinal - reserveStripeFeesFinal;
  const serviceFeeRateFinal = baseFareFinal > 0 ? (serviceFeeFinal / baseFareFinal) : (computed.serviceFeeRate ?? ((typeof serviceFeeRate === 'number' && Number.isFinite(serviceFeeRate)) ? serviceFeeRate : 0));

  const confNum = flightData.confirmation_number || flightData.airline_confirmation || booking.confirmation_number || '';
  // Actual cost overrides base fare for Accounting when set (what we paid for the flight)
  const actualCostVal = parseFloat(flightData._actual_cost ?? flightData.actual_cost ?? 0) || null;
  const baseFareForAccounting = (actualCostVal != null && actualCostVal > 0) ? actualCostVal : baseFareFinal;
  const flightCostOverage = (actualCostVal != null && actualCostVal > 0) ? Math.max(0, actualCostVal - baseFareFinal - milesPaidValue) : 0;
  // Cash the admin manually refunded to the customer (e.g. a partial
  // refund on a cancelled trip). Reduces recognized profit dollar-for-
  // dollar since that cash left Defera.
  const amountRefunded = parseFloat(flightData._amount_refunded ?? 0) || 0;
  // Post-booking seat purchases are charged on their own Stripe PI, outside
  // the installment plan, so none of the figures above see them. The term is
  // revenue MINUS the airline's pass-through cost in a single value, so the
  // two can never be booked independently — recognizing the customer's whole
  // payment as profit would overstate the trip by the seat price. Zero for
  // every booking without a paid seat purchase. See seatPurchaseEconomics.js.
  const netProfitFinalAdjusted = netProfitFinal - flightCostOverage - amountRefunded
    + seatPurchaseMarginContribution(flightData);
  return {
    id: booking.id,
    createdAt: booking.created_at,
    created_at: booking.created_at,
    airlineLogo,
    airlineCode,
    route,
    purchaseDate,
    departureDate,
    bookingReference: flightData.defera_booking_reference || booking.booking_reference || 'N/A',
    confirmationNumber: confNum,
    userName: displayName,
    userEmail: booking.user_email || '',
    paymentMethod,
    isMilesOnly,
    isMilesInstallments,
    creditsTotal,
    // Dollars of this trip's price paid with Defera Miles at checkout (all of
    // it on a trip booked with Miles alone), outside grandTotal otherwise.
    milesPaid: milesOnlyPrice ? milesOnlyPrice.total : milesPaidValue,
    milesUsed: snapshotMilesUsed,
    cashPortion: snapshotCashPortion,
    baseFare: baseFareForAccounting,
    baseFareOriginal: baseFareFinal,
    actualCost: actualCostVal,
    flightCostOverage,
    amountRefunded,
    serviceFeeRate: serviceFeeRateFinal,
    serviceFee: serviceFeeFinal,
    totalWithFee: totalWithFeeFinal,
    holdFee: holdFeeFinal,
    holdFeeRate: totalWithFeeFinal > 0 ? Math.round((holdFeeFinal / totalWithFeeFinal) * 100) / 100 : 0,
    grandTotal: grandTotalFinal,
    investorCost: investorCostFinal,
    backerId,
    backerFee: backerFeeFinal,
    milesEarned,
    mileageLiability: mileageLiabilityFinal,
    profit: profitFinal,
    netProfit: netProfitFinalAdjusted,
    isReserve,
    reserveCashPaymentCents: flightData._reserve_cash_payment_cents ?? flightData.reserve_cash_payment_cents ?? 0,
    reserveStripeFees: reserveStripeFeesFinal,
    status: booking.status || 'pending',
    finalRepaymentDate: booking.final_repayment_date || flightData.final_repayment_date || '',
    isTicketPurchased: accounting.is_ticket_purchased || !!(confNum && confNum.trim()),
    // Auto-derived: presence of a confirmation # implies the partner has reimbursed Defera.
    // Server also persists this in the background — keep client derivation as a fallback.
    isReimbursed: accounting.is_reimbursed || !!(confNum && confNum.trim()),
    isDisbursed: accounting.is_disbursed || false,
    rawFlightData: flightData,
    // New fields for T1/T2 SMS functionality
    passengerPhone,
    passengerTitle,
    destinationCity,
    hasReturn,
    t1Checked,
    t2Checked,
    isDepartureInFuture,
    isFlightHotelPackage,
    change_flight_history: booking.change_flight_history || [],
    archived_flight_data: booking.archived_flight_data,
    // Faye premium on this trip, outside every figure above (pass-through).
    insurancePremium,
  };
}

export default function Accounting() {
  const ic = useInstallmentsCopy();
  const installmentsCheckoutProvider = useInstallmentsCheckoutProvider();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [costSheetBooking, setCostSheetBooking] = useState(null);
  const [reserveTotalBalanceCents, setReserveTotalBalanceCents] = useState(0);
  const [deferaZeroTotalCollectedCents, setDeferaZeroTotalCollectedCents] = useState(0);
  const [reserveTripsCount, setReserveTripsCount] = useState(0);
  const [showAllReserveTripsModal, setShowAllReserveTripsModal] = useState(false);
  const [showAllZeroTripsModal, setShowAllZeroTripsModal] = useState(false);
  const [showProfitModal, setShowProfitModal] = useState(false);
  const [upcomingCapital, setUpcomingCapital] = useState([]);
  const [upcomingCapitalLoading, setUpcomingCapitalLoading] = useState(false);
  const [upcomingCapitalTotalCents, setUpcomingCapitalTotalCents] = useState(0);
  const [upcomingCapitalOutstandingCents, setUpcomingCapitalOutstandingCents] = useState(0);
  const [upcomingCapitalPaidCents, setUpcomingCapitalPaidCents] = useState(0);
  const [upcomingCapitalContractedCents, setUpcomingCapitalContractedCents] = useState(0);
  const [upcomingCapitalSubCount, setUpcomingCapitalSubCount] = useState(0);
  const [upcomingCapitalByProcessor, setUpcomingCapitalByProcessor] = useState(null);
  const [upcomingCapitalPlans, setUpcomingCapitalPlans] = useState({});
  /** Full trip value in flight. Read from the Cash Ladder's stored snapshot
   *  rather than recomputed here — one definition, one number, so this page
   *  and /cash-ladder can never disagree about the size of the book. */
  const [pipeline, setPipeline] = useState(null);
  const [pipelineGeneratedAt, setPipelineGeneratedAt] = useState(null);
  const [upcomingCapitalScheduledCents, setUpcomingCapitalScheduledCents] = useState(0);
  const [upcomingCapitalUnscheduledCents, setUpcomingCapitalUnscheduledCents] = useState(0);
  const [upcomingCapitalProfitCents, setUpcomingCapitalProfitCents] = useState(0);
  const [upcomingCapitalMargin, setUpcomingCapitalMargin] = useState(null);
  const [selectedUpcomingBucket, setSelectedUpcomingBucket] = useState(null);
  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const user = await User.me();
        if (!user || user.role !== 'admin') {
          toast.error('Admin access required');
          navigate('/');
          return;
        }
        setCurrentUser(user);
        fetchBookings();
        fetchUpcomingCapital();
        fetchPipeline();
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/');
      }
    };
    checkAdminAccess();
  }, [navigate]);

  const fetchPipeline = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      // Cheap: this reads the stored snapshot, it does not regenerate it.
      const response = await fetch('/api/admin/cash-ladder', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!response.ok) return;
      const json = await response.json();
      setPipeline(json?.snapshot?.payload?.pipeline || null);
      setPipelineGeneratedAt(json?.snapshot?.generated_at || null);
    } catch (error) {
      console.error('Pipeline value fetch failed:', error);
      // Non-blocking: the card hides itself and points at Cash Ladder.
    }
  };

  const fetchUpcomingCapital = async () => {
    setUpcomingCapitalLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const response = await fetch('/api/admin/upcoming-defera-capital-payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch upcoming Defera Capital payments');
      }
      const json = await response.json();
      setUpcomingCapital(json.upcoming || []);
      setUpcomingCapitalTotalCents(json.total_upcoming_cents || 0);
      setUpcomingCapitalOutstandingCents(json.total_outstanding_cents || 0);
      setUpcomingCapitalPaidCents(json.total_paid_cents || 0);
      setUpcomingCapitalContractedCents(json.total_contracted_cents || 0);
      setUpcomingCapitalSubCount(json.active_subscription_count || 0);
      setUpcomingCapitalByProcessor(json.by_processor || null);
      setUpcomingCapitalPlans(json.plans || {});
      setUpcomingCapitalScheduledCents(json.total_scheduled_cents || 0);
      setUpcomingCapitalUnscheduledCents(json.unscheduled_outstanding_cents || 0);
      setUpcomingCapitalProfitCents(json.total_scheduled_profit_cents || 0);
      setUpcomingCapitalMargin(json.margin_summary || null);
    } catch (error) {
      console.error('Upcoming Defera Capital fetch failed:', error);
      // Non-blocking: section just shows empty state
    } finally {
      setUpcomingCapitalLoading(false);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Authentication required');
        navigate('/');
        return;
      }

      const response = await fetch('/api/admin/accounting', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch accounting data');
      }

      const json = await response.json();
      const bookingsData = json.bookings || [];
      setReserveTotalBalanceCents(json.reserve_total_balance_cents ?? 0);
      setDeferaZeroTotalCollectedCents(json.defera_zero_total_collected_cents ?? 0);
      setReserveTripsCount(json.reserve_trips_count ?? 0);

      const processed = (bookingsData || []).map((b) => buildAccountingRow(b, { installmentsCheckoutProvider }));
      
      setBookings(processed);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const updateAccountingFlag = async (bookingId, field, value) => {
    setUpdating(prev => ({ ...prev, [`${bookingId}-${field}`]: true }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Authentication required');
        return;
      }

      const response = await fetch(`/api/admin/accounting/${bookingId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ field, value })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update status');
      }

      const { booking } = await response.json();
      const updatedFlightData = booking.flight_data;
      const updatedAccounting = updatedFlightData._accounting || {};

      setBookings(prev => prev.map(b => 
        b.id === bookingId 
          ? { 
              ...b, 
              isTicketPurchased: updatedAccounting.is_ticket_purchased || false,
              isReimbursed: updatedAccounting.is_reimbursed || false,
              isDisbursed: updatedAccounting.is_disbursed || false,
              t1Checked: updatedAccounting.t1_checked || false,
              t2Checked: updatedAccounting.t2_checked || false,
              rawFlightData: updatedFlightData 
            }
          : b
      ));
      
      const fieldLabels = {
        'is_ticket_purchased': 'Booked',
        'is_reimbursed': 'Reimbursed',
        'is_disbursed': 'Disbursed',
        't1_checked': 'T1',
        't2_checked': 'T2'
      };
      if (fieldLabels[field]) {
        toast.success(`${fieldLabels[field]} status updated`);
      }
    } catch (error) {
      console.error('Error updating flag:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(prev => ({ ...prev, [`${bookingId}-${field}`]: false }));
    }
  };

  // Handle T1/T2 checkbox - opens SMS compose and saves state
  const handleTripCheckbox = async (booking, checkboxType, checked) => {
    const fieldName = checkboxType === 'T1' ? 't1_checked' : 't2_checked';
    const fieldKey = `${booking.id}-${fieldName}`;
    
    // Optimistically update the UI immediately
    setBookings(prev => prev.map(b => 
      b.id === booking.id 
        ? { ...b, [checkboxType === 'T1' ? 't1Checked' : 't2Checked']: checked }
        : b
    ));
    
    if (!checked) {
      // If unchecking, just update the state without sending SMS
      await updateAccountingFlag(booking.id, fieldName, false);
      return;
    }
    
    // Compose and open SMS
    const message = composeSmsMessage(booking, checkboxType);
    const smsOpened = openSmsCompose(booking.passengerPhone, message, booking.bookingReference);
    
    // Save the checked state if SMS was opened successfully
    if (smsOpened) {
      try {
        await updateAccountingFlag(booking.id, fieldName, true);
      } catch (error) {
        // Revert on error
        setBookings(prev => prev.map(b => 
          b.id === booking.id 
            ? { ...b, [checkboxType === 'T1' ? 't1Checked' : 't2Checked']: false }
            : b
        ));
      }
    } else {
      // Revert if SMS didn't open
      setBookings(prev => prev.map(b => 
        b.id === booking.id 
          ? { ...b, [checkboxType === 'T1' ? 't1Checked' : 't2Checked']: false }
          : b
      ));
    }
  };

  const totals = useMemo(() => {
    const purchased = bookings.filter(b => b.isTicketPurchased);
    const reimbursed = bookings.filter(b => b.isReimbursed);
    const disbursed = bookings.filter(b => b.isDisbursed);
    
    const totalBaseFareSpent = purchased.reduce((sum, b) => sum + (b.baseFare || 0), 0);
    const totalWithFeesSpent = purchased.reduce((sum, b) => sum + (b.totalWithFee || 0), 0);
    const totalReimbursed = reimbursed.reduce((sum, b) => sum + (b.totalWithFee || 0), 0);
    const outstandingReimbursement = totalWithFeesSpent - totalReimbursed;
    // CRITICAL: Not Booked should only include trips that are not cancelled
    const pendingPurchase = bookings.filter(b => !b.isTicketPurchased && b.status !== 'cancelled' && b.status !== 'defaulted').reduce((sum, b) => sum + (b.baseFare || 0), 0);
    
    // New totals for expanded financial columns
    const totalHoldFee = bookings.reduce((sum, b) => sum + (b.holdFee || 0), 0);
    const totalGrand = bookings.reduce((sum, b) => sum + (b.grandTotal || 0), 0);
    const totalInvestorCost = bookings.reduce((sum, b) => sum + (b.investorCost || 0), 0);
    const totalBackerFees = bookings.reduce((sum, b) => sum + (b.backerFee || 0), 0);
    const totalMileageLiability = bookings.reduce((sum, b) => sum + (b.mileageLiability || 0), 0);
    const totalReserveStripeFees = bookings.reduce((sum, b) => sum + (b.reserveStripeFees || 0), 0);
    const totalProfit = bookings.reduce((sum, b) => sum + (b.profit || 0), 0);
    const totalNetProfit = bookings.reduce((sum, b) => sum + (b.netProfit || 0), 0);
    // Service Fees: sum per-booking; fallback to (sum of Total+Fees - sum of Base) when per-booking sum is 0
    let totalServiceFee = bookings.reduce((sum, b) => sum + (b.serviceFee || 0), 0);
    const sumTotalWithFeeAll = bookings.reduce((sum, b) => sum + (b.totalWithFee || 0), 0);
    const sumBaseFareAll = bookings.reduce((sum, b) => sum + (b.baseFare || 0), 0);
    if (totalServiceFee === 0 && sumTotalWithFeeAll > sumBaseFareAll) {
      totalServiceFee = sumTotalWithFeeAll - sumBaseFareAll;
    }
    const safeTotalServiceFee = Number.isFinite(totalServiceFee) ? totalServiceFee : 0;
    
    const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;
    const cancelledCount = bookings.filter(b => b.status === 'cancelled' || b.status === 'refunded').length;
    const completedCount = bookings.filter(b => b.status === 'completed').length;
    const defaultedCount = bookings.filter(b => b.status === 'defaulted').length;
    const ticketedCount = bookings.filter(b => b.isTicketPurchased).length;
    const reserveBookingsCount = bookings.filter(b => b.paymentMethod === 'reserve').length;
    const deferaZeroCount = bookings.filter(b => b.paymentMethod === 'defera_zero').length;
    const totalBookings = bookings.length;
    const confirmedPercentage = totalBookings > 0 ? ((confirmedCount / totalBookings) * 100).toFixed(1) : 0;
    const cancelledPercentage = totalBookings > 0 ? ((cancelledCount / totalBookings) * 100).toFixed(1) : 0;
    const completedPercentage = totalBookings > 0 ? ((completedCount / totalBookings) * 100).toFixed(1) : 0;
    const defaultedPercentage = totalBookings > 0 ? ((defaultedCount / totalBookings) * 100).toFixed(1) : 0;
    const ticketedPercentage = totalBookings > 0 ? ((ticketedCount / totalBookings) * 100).toFixed(1) : 0;
    const reserveCount = reserveBookingsCount + (reserveTripsCount || 0);
    const totalItems = totalBookings + (reserveTripsCount || 0);
    const reservePercentage = totalItems > 0 ? ((reserveCount / totalItems) * 100).toFixed(1) : 0;
    const deferaZeroPercentage = totalBookings > 0 ? ((deferaZeroCount / totalBookings) * 100).toFixed(1) : 0;

    return {
      totalBaseFareSpent,
      totalWithFeesSpent,
      totalReimbursed,
      outstandingReimbursement,
      pendingPurchase,
      purchasedCount: purchased.length,
      reimbursedCount: reimbursed.length,
      disbursedCount: disbursed.length,
      // CRITICAL: Not Booked count should only include trips that are not cancelled/defaulted
      pendingCount: bookings.filter(b => !b.isTicketPurchased && b.status !== 'cancelled' && b.status !== 'defaulted').length,
      confirmedCount,
      cancelledCount,
      completedCount,
      defaultedCount,
      ticketedCount,
      reserveCount,
      deferaZeroCount,
      confirmedPercentage,
      cancelledPercentage,
      completedPercentage,
      defaultedPercentage,
      ticketedPercentage,
      reservePercentage,
      deferaZeroPercentage,
      totalHoldFee,
      totalGrand,
      totalInvestorCost,
      totalBackerFees,
      totalMileageLiability,
      totalProfit,
      totalNetProfit,
      totalServiceFee: safeTotalServiceFee
    };
  }, [bookings, reserveTripsCount]);

  // Group upcoming Defera Capital date-buckets into weeks (Sunday-Saturday).
  // Emits a `week` row at the end of each week with the subtotal — matches the user request:
  // "$X expected week X" line under each week's last date.
  const upcomingCapitalByWeek = useMemo(() => {
    const weeks = [];
    let current = null;
    for (const bucket of upcomingCapital) {
      const date = new Date(`${bucket.date}T12:00:00Z`);
      const day = date.getUTCDay(); // 0 = Sunday
      const sunday = new Date(date);
      sunday.setUTCDate(date.getUTCDate() - day);
      const weekKey = sunday.toISOString().slice(0, 10);
      if (!current || current.weekKey !== weekKey) {
        const saturday = new Date(sunday);
        saturday.setUTCDate(sunday.getUTCDate() + 6);
        current = {
          weekKey,
          weekStart: sunday.toISOString().slice(0, 10),
          weekEnd: saturday.toISOString().slice(0, 10),
          buckets: [],
          totalCents: 0,
          profitCents: 0,
          count: 0,
        };
        weeks.push(current);
      }
      current.buckets.push(bucket);
      current.totalCents += bucket.total_cents;
      current.profitCents += bucket.profit_cents || 0;
      current.count += bucket.count;
    }
    return weeks;
  }, [upcomingCapital]);

  const profitByMonth = useMemo(() => {
    const byMonth = new Map();
    for (const b of bookings) {
      const d = (b.createdAt || b.created_at) ? new Date(b.createdAt || b.created_at) : new Date(0);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth.has(key)) {
        byMonth.set(key, []);
      }
      byMonth.get(key).push(b);
    }
    return Array.from(byMonth.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, items]) => {
        const [y, m] = key.split('-');
        const monthName = new Date(parseInt(y, 10), parseInt(m, 10) - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
        const totalBaseFare = items.reduce((s, i) => s + (i.baseFare || 0), 0);
        const totalServiceFee = items.reduce((s, i) => s + (i.serviceFee || 0), 0);
        const totalHoldFee = items.reduce((s, i) => s + (i.holdFee || 0), 0);
        const totalInvestorCost = items.reduce((s, i) => s + (i.investorCost || 0), 0);
        const totalMileageLiability = items.reduce((s, i) => s + (i.mileageLiability || 0), 0);
        const totalProfit = items.reduce((s, i) => s + (i.profit || 0), 0);
        const totalNetProfit = items.reduce((s, i) => s + (i.netProfit || 0), 0);
        return { key, monthName, items, totalBaseFare, totalServiceFee, totalHoldFee, totalInvestorCost, totalMileageLiability, totalProfit, totalNetProfit };
      });
  }, [bookings]);

  const handleRowClick = (booking, e) => {
    if (e.target.closest('button') || e.target.closest('[role="checkbox"]') || e.target.type === 'checkbox') {
      return;
    }
    sessionStorage.setItem('open_booking_id', booking.id);
    navigate('/trips');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white relative">
      <RoyalAmbientBackground />
      <div className="mx-auto px-1 sm:px-2 lg:px-[72px] py-4 relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-white">Accounting</h1>
            <p className="text-[10px] text-gray-500">Financial Overview</p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/AccountingRewards')}
              className="h-7 px-2 text-xs text-gray-400 hover:text-white"
            >
              <Gift className="h-3 w-3 mr-1" />
              Rewards
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowProfitModal(true)}
              className="h-7 px-2 text-xs text-gray-400 hover:text-white"
            >
              <DollarSign className="h-3 w-3 mr-1" />
              Profit
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { fetchBookings(); fetchUpcomingCapital(); fetchPipeline(); }}
              className="h-7 px-2 text-xs text-gray-400 hover:text-white"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-6">
          <div className="rounded-xl bg-white/[0.02] px-3 py-2.5 sm:px-4 sm:py-3 backdrop-blur-sm min-w-0">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase mb-1">Base Spent</p>
            <p className="text-base font-semibold text-white tabular-nums">${formatCurrency(totals.totalBaseFareSpent)}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500/80 mt-0.5 sm:mt-1">{totals.purchasedCount} bookings</p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2.5 sm:px-4 sm:py-3 backdrop-blur-sm min-w-0">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase mb-1">Service Fees</p>
            <p className="text-base font-semibold text-emerald-400/90 tabular-nums">${formatCurrency(totals.totalServiceFee)}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500/80 mt-0.5 sm:mt-1">Total earned</p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2.5 sm:px-4 sm:py-3 backdrop-blur-sm min-w-0">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase mb-1">Total + Fees</p>
            <p className="text-base font-semibold text-white tabular-nums">${formatCurrency(totals.totalWithFeesSpent)}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500/80 mt-0.5 sm:mt-1">Base + Service</p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2.5 sm:px-4 sm:py-3 backdrop-blur-sm min-w-0">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase mb-1">Reimbursed</p>
            <p className="text-base font-semibold text-emerald-400/90 tabular-nums">${formatCurrency(totals.totalReimbursed)}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500/80 mt-0.5 sm:mt-1">{totals.reimbursedCount} complete</p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2.5 sm:px-4 sm:py-3 backdrop-blur-sm min-w-0">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase mb-1">Not Booked</p>
            <p className={`text-base font-semibold tabular-nums ${totals.pendingPurchase > 0 ? 'text-red-400/90' : 'text-emerald-400/90'}`}>
              ${formatCurrency(totals.pendingPurchase)}
            </p>
            <p className="text-[9px] sm:text-[10px] text-gray-500/80 mt-0.5 sm:mt-1">{totals.pendingCount} pending</p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2.5 sm:px-4 sm:py-3 backdrop-blur-sm min-w-0">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase mb-1">Outstanding</p>
            <p className={`text-base font-semibold tabular-nums ${totals.outstandingReimbursement > 0 ? 'text-brand-400/90' : 'text-emerald-400/90'}`}>
              ${formatCurrency(totals.outstandingReimbursement)}
            </p>
            <p className="text-[9px] sm:text-[10px] text-gray-500/80 mt-0.5 sm:mt-1">{ic.awaitingPartner}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-6">
          <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 px-3 py-2.5 sm:px-4 sm:py-3 backdrop-blur-sm min-w-0 relative">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-emerald-400/80 font-normal tracking-wider uppercase mb-0.5 sm:mb-1">Defera Reserve</p>
                <p className="text-sm sm:text-base font-medium text-emerald-400 tabular-nums truncate">${formatCurrency((reserveTotalBalanceCents || 0) / 100)}</p>
                <p className="text-[9px] sm:text-[10px] text-gray-500/80 mt-0.5 sm:mt-1 line-clamp-2">Total credits held by customers</p>
              </div>
              <button
                type="button"
                onClick={() => setShowAllReserveTripsModal(true)}
                className="flex-shrink-0 p-1.5 rounded-lg text-emerald-400/70 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                title="All Reserve Trips"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="rounded-xl bg-brand-500/5 border border-brand-500/20 px-3 py-2.5 sm:px-4 sm:py-3 backdrop-blur-sm min-w-0 relative">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-brand-400/80 font-normal tracking-wider uppercase mb-0.5 sm:mb-1">Defera Zero</p>
                <p className="text-sm sm:text-base font-medium text-brand-400 tabular-nums truncate">${formatCurrency((deferaZeroTotalCollectedCents || 0) / 100)}</p>
                <p className="text-[9px] sm:text-[10px] text-gray-500/80 mt-0.5 sm:mt-1 line-clamp-2">Total paid by customers on DZ trips</p>
              </div>
              <button
                type="button"
                onClick={() => setShowAllZeroTripsModal(true)}
                className="flex-shrink-0 p-1.5 rounded-lg text-brand-400/70 hover:text-brand-400 hover:bg-brand-500/10 transition-colors"
                title="All Defera Zero Trips"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Pipeline — the FULL grand total of every trip still in flight, not the
            remaining balance. Answers "how much business is in the air", which
            neither collected nor outstanding answers on its own.

            Sourced from the Cash Ladder snapshot (server/pipeline-value.js) so
            both pages read one stored number. It regenerates only when someone
            presses Refresh on /cash-ladder, hence the as-of stamp. */}
        {pipeline && (
          <div className="rounded-xl bg-white/[0.03] border border-white/10 px-3 py-3 sm:px-4 sm:py-4 backdrop-blur-sm mb-6">
            <div className="flex items-start justify-between gap-2 mb-3">
              <p className="text-[10px] text-gray-400/80 font-normal tracking-wider uppercase">
                Pipeline — full trip value in flight
              </p>
              {pipelineGeneratedAt && (
                <Link
                  to="/cash-ladder"
                  className="text-[9px] sm:text-[10px] text-gray-500 hover:text-gray-300 transition shrink-0"
                >
                  as of {new Date(pipelineGeneratedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · Cash Ladder →
                </Link>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              <div>
                <p className="text-lg sm:text-xl font-medium text-white/90 tabular-nums">
                  ${formatCurrency(pipeline.total_cents / 100)}
                </p>
                <p className="text-[9px] sm:text-[10px] text-gray-500/90 mt-0.5 leading-tight">
                  Total trip value
                  <span className="text-gray-600"> · {pipeline.trips} trips not paid off</span>
                </p>
              </div>
              <div>
                <p className="text-sm sm:text-base font-medium text-emerald-300/90 tabular-nums">
                  ${formatCurrency(pipeline.paid_cents / 100)}
                </p>
                <p className="text-[9px] sm:text-[10px] text-gray-500/90 mt-0.5 leading-tight">
                  Collected
                  {pipeline.collected_share != null && (
                    <span className="text-gray-600"> · {(pipeline.collected_share * 100).toFixed(1)}% of the book</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm sm:text-base font-medium text-amber-300/90 tabular-nums">
                  ${formatCurrency(pipeline.outstanding_cents / 100)}
                </p>
                <p className="text-[9px] sm:text-[10px] text-gray-500/90 mt-0.5 leading-tight">
                  Still to collect
                  {pipeline.receivable_outstanding_cents !== pipeline.outstanding_cents && (
                    <span className="text-gray-600"> · ${formatCurrency(pipeline.receivable_outstanding_cents / 100)} owed to us</span>
                  )}
                  {pipeline.components?.insurance_outstanding_cents > 0 && (
                    <span className="text-gray-600"> · incl. ${formatCurrency(pipeline.components.insurance_outstanding_cents / 100)} Faye insurance</span>
                  )}
                </p>
              </div>
            </div>

            {/* Per-product split — the total spans Capital, Zero and the legacy
                Slice Pay book, which behave differently enough to name. */}
            {pipeline.buckets?.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                {pipeline.buckets.map((b) => (
                  <span key={b.key} className="text-[9px] sm:text-[10px] text-gray-500/90">
                    <span className="text-gray-400">{b.label}</span>{' '}
                    <span className="tabular-nums text-gray-300">${formatCurrency(b.total_cents / 100)}</span>
                    <span className="text-gray-600"> ({b.trips})</span>
                    {!b.is_receivable && <span className="text-gray-600"> · processor-funded</span>}
                  </span>
                ))}
              </div>
            )}

            {/* What the total is made of. `other` is hotel on packages plus
                seats financed at checkout — neither lives in the flight fare. */}
            {pipeline.components && (
              <p className="text-[9px] sm:text-[10px] text-gray-600 mt-2 leading-tight">
                airfare ${formatCurrency(pipeline.components.fare_cents / 100)}
                {' · '}service ${formatCurrency(pipeline.components.service_fee_cents / 100)}
                {' · '}hold ${formatCurrency(pipeline.components.hold_fee_cents / 100)}
                {pipeline.components.other_cents !== 0 && (
                  <> · hotel + seats ${formatCurrency(pipeline.components.other_cents / 100)}</>
                )}
                {pipeline.components.insurance_cents > 0 && (
                  <> · plus ${formatCurrency(pipeline.components.insurance_cents / 100)} Faye insurance collected for Faye (outside the total)</>
                )}
              </p>
            )}
          </div>
        )}

        {/* Defera Capital — upcoming installment payments, BOTH rails (live Stripe
            subscriptions + our own NMI installment schedule), grouped by due date. */}
        <div className="rounded-xl bg-brand-500/5 border border-brand-500/20 px-3 py-3 sm:px-4 sm:py-4 backdrop-blur-sm mb-6">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] text-brand-400/80 font-normal tracking-wider uppercase mb-1">Defera Capital — Upcoming Payments</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                <div>
                  <p className="text-sm sm:text-base font-medium text-brand-300 tabular-nums">
                    ${formatCurrency(upcomingCapitalTotalCents / 100)}
                  </p>
                  <p className="text-[9px] sm:text-[10px] text-gray-500/90 mt-0.5 leading-tight">
                    Next installments
                    <span className="text-gray-600"> · sum of next charge per plan</span>
                  </p>
                </div>
                <div>
                  <p className="text-sm sm:text-base font-medium text-brand-300/90 tabular-nums">
                    ${formatCurrency(upcomingCapitalOutstandingCents / 100)}
                  </p>
                  <p className="text-[9px] sm:text-[10px] text-gray-500/90 mt-0.5 leading-tight">
                    Total outstanding
                    <span className="text-gray-600"> · still owed across {upcomingCapitalSubCount} plan{upcomingCapitalSubCount === 1 ? '' : 's'}</span>
                    {upcomingCapitalUnscheduledCents > 0 && (
                      <span className="text-amber-300/70"> · ${formatCurrency(upcomingCapitalUnscheduledCents / 100)} on no schedule</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm sm:text-base font-medium text-emerald-300/90 tabular-nums">
                    ${formatCurrency(upcomingCapitalPaidCents / 100)}
                  </p>
                  <p className="text-[9px] sm:text-[10px] text-gray-500/90 mt-0.5 leading-tight">
                    Paid to date
                    {upcomingCapitalContractedCents > 0 && (
                      <span className="text-gray-600"> · of ${formatCurrency(upcomingCapitalContractedCents / 100)} contracted</span>
                    )}
                  </p>
                </div>
              </div>
              {/* Rail split — the card covers both processors, so say so with numbers. */}
              {upcomingCapitalByProcessor && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                  {[
                    { key: 'stripe', label: 'Stripe' },
                    { key: 'nmi', label: 'NMI' },
                  ].map(({ key, label }) => {
                    const rail = upcomingCapitalByProcessor[key];
                    if (!rail) return null;
                    return (
                      <span key={key} className="text-[9px] sm:text-[10px] text-gray-500/90 leading-tight">
                        <span className="uppercase tracking-wider text-gray-400">{label}</span>
                        <span className="text-gray-600"> · {rail.plan_count} plan{rail.plan_count === 1 ? '' : 's'} · </span>
                        <span className="tabular-nums text-gray-400">${formatCurrency(rail.next_installments_cents / 100)}</span>
                        <span className="text-gray-600"> next · </span>
                        <span className="tabular-nums text-gray-400">${formatCurrency(rail.outstanding_cents / 100)}</span>
                        <span className="text-gray-600"> outstanding</span>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            {upcomingCapitalLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-brand-400/70 flex-shrink-0" />
            ) : (
              <Calendar className="h-4 w-4 text-brand-400/70 flex-shrink-0" />
            )}
          </div>

          {upcomingCapital.length === 0 ? (
            <p className="text-[11px] text-gray-400">
              {upcomingCapitalLoading ? 'Loading Stripe and NMI schedules…' : 'No upcoming Defera Capital payments scheduled.'}
            </p>
          ) : (
            <>
            {/* Says what the calendar below is: every remaining charge, not one per plan. */}
            <p className="text-[9px] sm:text-[10px] text-gray-500/90 mb-2 leading-tight">
              Full schedule
              <span className="text-gray-600"> · every remaining charge through </span>
              <span className="text-gray-400">
                {new Date(`${upcomingCapital[upcomingCapital.length - 1].date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </span>
              <span className="text-gray-600"> · </span>
              <span className="text-gray-400 tabular-nums">${formatCurrency(upcomingCapitalScheduledCents / 100)}</span>
              <span className="text-gray-600"> of ${formatCurrency(upcomingCapitalOutstandingCents / 100)} outstanding · </span>
              <span className="text-emerald-400/90 tabular-nums">${formatCurrency(upcomingCapitalProfitCents / 100)}</span>
              <span className="text-gray-600"> profit</span>
            </p>
            {/* Margin is only as good as its inputs — say which basis produced it. */}
            {upcomingCapitalMargin && (
              <p className="text-[9px] sm:text-[10px] text-gray-500/90 mb-2 -mt-1 leading-tight">
                <span className="text-gray-600">Profit = service fee + hold fee where the trip total beats what we paid the airline · </span>
                <span className="text-gray-400">{upcomingCapitalMargin.plans_with_actual_cost}</span>
                <span className="text-gray-600"> plan{upcomingCapitalMargin.plans_with_actual_cost === 1 ? '' : 's'} priced on actual cost, </span>
                <span className="text-gray-400">{upcomingCapitalMargin.plans_service_fee_only}</span>
                <span className="text-gray-600"> on service fee alone</span>
                {upcomingCapitalMargin.unrecognized_hold_cents > 0 && (
                  <>
                    <span className="text-gray-600"> · </span>
                    <span className="text-gray-400 tabular-nums">${formatCurrency(upcomingCapitalMargin.unrecognized_hold_cents / 100)}</span>
                    <span className="text-gray-600"> of hold fees not yet counted</span>
                  </>
                )}
                {upcomingCapitalMargin.negative_margin_plan_count > 0 && (
                  <>
                    <span className="text-gray-600"> · </span>
                    <span className="text-red-400/80">{upcomingCapitalMargin.negative_margin_plan_count} underwater</span>
                  </>
                )}
              </p>
            )}
            <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
              {upcomingCapitalByWeek.map((week, weekIdx) => {
                const weekStartObj = new Date(`${week.weekStart}T12:00:00Z`);
                const weekEndObj = new Date(`${week.weekEnd}T12:00:00Z`);
                const weekLabel = `${weekStartObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekEndObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                return (
                  <React.Fragment key={week.weekKey}>
                    {week.buckets.map((bucket) => {
                      const dateObj = new Date(`${bucket.date}T12:00:00Z`);
                      const dateLabel = dateObj.toLocaleDateString('en-US', {
                        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
                      });
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isPast = dateObj < today;
                      return (
                        <button
                          key={bucket.date}
                          type="button"
                          onClick={() => setSelectedUpcomingBucket(bucket)}
                          className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg border transition-colors text-left ${
                            isPast
                              ? 'bg-brand-500/5 border-brand-500/20 hover:bg-brand-500/10'
                              : 'bg-white/[0.02] border-white/5 hover:bg-brand-500/10 hover:border-brand-500/30'
                          }`}
                          title="Click to see installment plans"
                        >
                          <div className="min-w-0 flex items-center gap-2">
                            <span className={`text-[11px] tabular-nums font-normal ${isPast ? 'text-brand-300' : 'text-white'}`}>
                              ${formatCurrency(bucket.total_cents / 100)}
                            </span>
                            <span className="text-[10px] text-gray-400">expected</span>
                            <span className={`text-[11px] tabular-nums ${isPast ? 'text-brand-300/80' : 'text-gray-300'}`}>
                              {dateLabel}
                            </span>
                            {isPast && (
                              <span className="text-[9px] uppercase tracking-wider text-brand-400/80 font-normal">overdue</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Margin inside that day's cash — pro-rata share of each plan's trip margin. */}
                            <span
                              className={`text-[11px] tabular-nums ${bucket.profit_cents < 0 ? 'text-red-400/90' : 'text-emerald-400/90'}`}
                              title="Profit inside this day's collections"
                            >
                              {bucket.profit_cents < 0 ? '−' : ''}${formatCurrency(Math.abs(bucket.profit_cents) / 100)}
                            </span>
                            <span className="text-[10px] text-gray-500">profit</span>
                            <span className="text-[10px] text-gray-500">·</span>
                            <span className="text-[10px] text-gray-500">{bucket.count} plan{bucket.count === 1 ? '' : 's'}</span>
                            <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
                          </div>
                        </button>
                      );
                    })}
                    {/* Weekly subtotal — sits beneath the last day of each week */}
                    <div className="flex items-center justify-between gap-3 px-3 py-1.5 border-l-2 border-brand-500/40 bg-brand-500/5 rounded-r ml-1">
                      <div className="min-w-0 flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider text-brand-300/90 font-normal">
                          Week {weekIdx + 1}
                        </span>
                        <span className="text-[10px] text-gray-500">{weekLabel}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[11px] text-brand-200 font-medium tabular-nums">
                          ${formatCurrency(week.totalCents / 100)}
                        </span>
                        <span className="text-[9px] text-gray-500">expected</span>
                        <span className={`text-[11px] font-medium tabular-nums ${week.profitCents < 0 ? 'text-red-400/90' : 'text-emerald-400/90'}`}>
                          {week.profitCents < 0 ? '−' : ''}${formatCurrency(Math.abs(week.profitCents) / 100)}
                        </span>
                        <span className="text-[9px] text-gray-500">profit</span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-3 mb-6">
          <div className="rounded-xl bg-white/[0.02] px-3 py-2 sm:px-4 sm:py-2.5 backdrop-blur-sm min-w-[70px] sm:min-w-[100px]">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase">Confirmed</p>
            <p className="text-sm font-medium text-emerald-400/90 mt-0.5 tabular-nums">
              {totals.confirmedCount} <span className="text-gray-500 font-normal">({totals.confirmedPercentage}%)</span>
            </p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2 sm:px-4 sm:py-2.5 backdrop-blur-sm min-w-[70px] sm:min-w-[100px]">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase">Cancelled</p>
            <p className="text-sm font-medium text-red-400/90 mt-0.5 tabular-nums">
              {totals.cancelledCount} <span className="text-gray-500 font-normal">({totals.cancelledPercentage}%)</span>
            </p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2 sm:px-4 sm:py-2.5 backdrop-blur-sm min-w-[70px] sm:min-w-[100px]">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase">Completed</p>
            <p className="text-sm font-medium text-brand-400/90 mt-0.5 tabular-nums">
              {totals.completedCount} <span className="text-gray-500 font-normal">({totals.completedPercentage}%)</span>
            </p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2 sm:px-4 sm:py-2.5 backdrop-blur-sm min-w-[70px] sm:min-w-[100px]">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase">Defaulted</p>
            <p className="text-sm font-medium text-orange-400/90 mt-0.5 tabular-nums">
              {totals.defaultedCount} <span className="text-gray-500 font-normal">({totals.defaultedPercentage}%)</span>
            </p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2 sm:px-4 sm:py-2.5 backdrop-blur-sm min-w-[70px] sm:min-w-[100px]">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase">Ticketed</p>
            <p className="text-sm font-medium text-brand-400/90 mt-0.5 tabular-nums">
              {totals.ticketedCount} <span className="text-gray-500 font-normal">({totals.ticketedPercentage}%)</span>
            </p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2 sm:px-4 sm:py-2.5 backdrop-blur-sm min-w-[70px] sm:min-w-[100px]">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase">Defera Reserve</p>
            <p className="text-sm font-medium text-emerald-400/90 mt-0.5 tabular-nums">
              {totals.reserveCount} <span className="text-gray-500 font-normal">({totals.reservePercentage}%)</span>
            </p>
          </div>
          <div className="rounded-xl bg-white/[0.02] px-3 py-2 sm:px-4 sm:py-2.5 backdrop-blur-sm min-w-[70px] sm:min-w-[100px]">
            <p className="text-[10px] text-gray-500 font-normal tracking-wider uppercase">Defera Zero</p>
            <p className="text-sm font-medium text-cyan-400/90 mt-0.5 tabular-nums">
              {totals.deferaZeroCount} <span className="text-gray-500 font-normal">({totals.deferaZeroPercentage}%)</span>
            </p>
          </div>
        </div>

        <div className="hidden md:block bg-[#12121a] border border-gray-800/50 rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-[11px]" style={{ tableLayout: 'auto' }}>
            <thead>
              <tr className="bg-[#0d0d12] border-b border-gray-800/50">
                <th className="text-center py-2 px-1 text-gray-500 font-medium whitespace-nowrap min-w-[32px]">Air</th>
                <th className="text-left py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Route</th>
                <th className="text-left py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Purchase</th>
                <th className="text-left py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Departure</th>
                <th className="text-left py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Ref</th>
                <th className="text-left py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Conf #</th>
                <th className="text-left py-2 px-1 text-gray-500 font-medium whitespace-nowrap">User</th>
                <th className="text-right py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Base</th>
                <th className="text-right py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Service</th>
                <th className="text-right py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Total</th>
                <th className="text-right py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Hold</th>
                <th className="text-right py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Grand</th>
                <th className="text-right py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Profit</th>
                <th className="text-right py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Investor</th>
                <th className="text-center py-2 px-1 text-gray-500 font-medium whitespace-nowrap w-10">Method</th>
                <th className="text-right py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Miles</th>
                <th className="text-right py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Net</th>
                <th className="text-center py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Status</th>
                <th className="text-left py-2 px-1 text-gray-500 font-medium whitespace-nowrap">Final Repay</th>
                <th className="text-center py-2 px-1 text-gray-500 font-medium whitespace-nowrap w-8">Bkd</th>
                <th className="text-center py-2 px-1 text-gray-500 font-medium whitespace-nowrap w-8">Pd</th>
                <th className="text-center py-2 px-1 text-gray-500 font-medium whitespace-nowrap w-8">Rel</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking, idx) => {
                const alertClass = getRowAlertClass(booking);
                return (
                <tr 
                  key={booking.id} 
                  onClick={(e) => handleRowClick(booking, e)}
                  className={`border-b border-gray-800/30 transition-colors cursor-pointer ${alertClass || (idx % 2 === 0 ? 'bg-[#0f0f15] hover:bg-[#1a1a25]' : 'hover:bg-[#1a1a25]')}`}
                >
                  <td className="py-1 px-1 text-center min-w-[32px]">
                    {booking.airlineLogo ? (
                      <img 
                        src={booking.airlineLogo} 
                        alt={booking.airlineCode} 
                        className="h-5 w-5 object-contain mx-auto"
                        onError={(e) => { 
                          e.target.style.display = 'none'; 
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'inline';
                        }}
                      />
                    ) : null}
                    <span 
                      className={`text-[9px] text-gray-400 font-mono`}
                      style={{ display: booking.airlineLogo ? 'none' : 'inline' }}
                    >
                      {booking.airlineCode || '—'}
                    </span>
                  </td>
                  <td className="py-1.5 px-1 text-white font-medium whitespace-nowrap">{booking.route}</td>
                  <td className="py-1.5 px-1 text-white whitespace-nowrap">{booking.purchaseDate}</td>
                  <td className="py-1.5 px-1 text-white whitespace-nowrap">{booking.departureDate}</td>
                  <td className="py-1.5 px-1 text-white font-mono text-[10px] whitespace-nowrap">{booking.bookingReference}</td>
                  <td className="py-1.5 px-1 text-white font-mono text-[10px] whitespace-nowrap">{booking.confirmationNumber || '—'}</td>
                  <td className="py-1.5 px-1 text-white truncate max-w-[100px]" title={booking.userEmail}>{booking.userName}</td>
                  <td className="py-1.5 px-1 text-right text-white whitespace-nowrap">${formatCurrency(booking.baseFare)}</td>
                  <td className="py-1.5 px-1 text-right whitespace-nowrap">
                    {booking.isFlightHotelPackage ? (
                      <span className="text-green-400 text-[10px]">Waived</span>
                    ) : (
                      <>${formatCurrency(booking.serviceFee ?? 0)} <span className="text-gray-500 text-[9px]">{Number.isFinite(booking.serviceFeeRate) ? `(${Math.round((booking.serviceFeeRate ?? 0) * 100)}%)` : ''}</span></>
                    )}
                  </td>
                  <td className="py-1.5 px-1 text-right text-white whitespace-nowrap">${formatCurrency(booking.totalWithFee)}</td>
                  <td className="py-1.5 px-1 text-right text-white whitespace-nowrap">${formatCurrency(booking.holdFee)}</td>
                  <td className="py-1.5 px-1 text-right text-white font-medium whitespace-nowrap">${formatCurrency(booking.grandTotal)}</td>
                  <td className="py-1.5 px-1 text-right text-emerald-400 whitespace-nowrap">${formatCurrency(booking.profit)}</td>
                  <td className="py-1.5 px-1 text-right text-red-400 whitespace-nowrap">${formatCurrency(booking.investorCost)}</td>
                  <td className="py-1.5 px-1 text-center whitespace-nowrap">
                    {booking.paymentMethod === 'reserve' ? (
                      <span className="inline-flex items-center px-1 py-0.5 rounded text-[7px] font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" title="Reserve">R</span>
                    ) : booking.paymentMethod === 'defera_zero' ? (
                      <span className="inline-flex items-center px-1 py-0.5 rounded text-[7px] font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" title="Defera Zero">DZ</span>
                    ) : (
                      <span className="inline-flex items-center px-1 py-0.5 rounded text-[7px] font-medium bg-white/10 text-white/70 border border-white/20" title="Installments">IN</span>
                    )}
                  </td>
                  <td className="py-1.5 px-1 text-right text-red-400 whitespace-nowrap" title={`${formatCurrency(booking.milesEarned)} miles earned`}>
                    ${formatCurrency(booking.mileageLiability)}
                  </td>
                  <td className={`py-1.5 px-1 text-right font-medium whitespace-nowrap ${booking.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>${formatCurrency(booking.netProfit)}</td>
                  <td className="py-1.5 px-1 text-center whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 justify-center">
                      {(booking.change_flight_history?.length > 0 || booking.archived_flight_data) && (
                        <span className="text-brand-400/90" title="Flight changed by customer">
                          <ArrowLeftRight className="w-3.5 h-3.5" />
                        </span>
                      )}
                      {getStatusBadge(booking.status)}
                    </span>
                  </td>
                  <td className="py-1.5 px-1 text-white text-[10px] whitespace-nowrap">
                    {booking.finalRepaymentDate ? new Date(booking.finalRepaymentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </td>
                  <td className="py-1.5 px-1 text-center w-8">
                    <Checkbox
                      checked={booking.isTicketPurchased}
                      onCheckedChange={(checked) => updateAccountingFlag(booking.id, 'is_ticket_purchased', checked)}
                      className="h-3.5 w-3.5 border-gray-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                    />
                  </td>
                  <td className="py-1.5 px-1 text-center w-8">
                    <Checkbox
                      checked={booking.isReimbursed}
                      onCheckedChange={(checked) => updateAccountingFlag(booking.id, 'is_reimbursed', checked)}
                      className="h-3.5 w-3.5 border-gray-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                    />
                  </td>
                  <td className="py-1.5 px-1 text-center w-8">
                    <Checkbox
                      checked={booking.isDisbursed}
                      onCheckedChange={(checked) => updateAccountingFlag(booking.id, 'is_disbursed', checked)}
                      className="h-3.5 w-3.5 border-gray-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                    />
                  </td>
                </tr>
              );
              })}
              {bookings.length === 0 && (
                <tr>
                  <td colSpan={22} className="py-8 text-center text-gray-500 text-xs">No bookings found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-2">
          {bookings.map((booking) => {
            const alertClass = getRowAlertClass(booking);
            return (
            <div 
              key={booking.id}
              className={`border border-gray-800/50 rounded-lg p-2 transition-colors ${alertClass || 'bg-[#12121a]'}`}
            >
              <div 
                className="cursor-pointer"
                onClick={(e) => handleRowClick(booking, e)}
              >
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    {booking.airlineLogo ? (
                      <img 
                        src={booking.airlineLogo} 
                        alt={booking.airlineCode} 
                        className="h-4 w-4 object-contain flex-shrink-0"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : booking.airlineCode ? (
                      <span className="text-[8px] text-gray-500 font-mono bg-gray-800 px-1 rounded flex-shrink-0">{booking.airlineCode}</span>
                    ) : null}
                    <span className="text-xs font-medium text-white truncate">{booking.route}</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap flex-shrink-0">
                    {booking.paymentMethod === 'reserve' ? (
                      <span className="inline-flex items-center px-1 py-0.5 rounded text-[7px] font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" title="Reserve">R</span>
                    ) : booking.paymentMethod === 'defera_zero' ? (
                      <span className="inline-flex items-center px-1 py-0.5 rounded text-[7px] font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" title="Defera Zero">DZ</span>
                    ) : (
                      <span className="inline-flex items-center px-1 py-0.5 rounded text-[7px] font-medium bg-white/10 text-white/70 border border-white/20" title="Installments">IN</span>
                    )}
                    {(booking.change_flight_history?.length > 0 || booking.archived_flight_data) && (
                      <span className="text-brand-400/90" title="Flight changed by customer">
                        <ArrowLeftRight className="w-3.5 h-3.5" />
                      </span>
                    )}
                    {getStatusBadge(booking.status)}
                    <span className="text-[10px] font-mono text-white">{booking.bookingReference}</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-x-2 text-[10px] text-white mb-2">
                  <div>
                    <span className="text-gray-500">Dept:</span> {booking.departureDate}
                  </div>
                  <div>
                    <span className="text-gray-500">Repay:</span> {booking.finalRepaymentDate ? new Date(booking.finalRepaymentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </div>
                  <div className="truncate" title={booking.userEmail}>
                    <span className="text-gray-500">User:</span> {booking.userName}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[10px]">
                  <div>
                    <span className="text-gray-500">Net:</span>
                    <span className={`ml-1 font-medium ${booking.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>${formatCurrency(booking.netProfit)}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCostSheetBooking(booking);
                    }}
                    className="flex items-center gap-0.5 text-[9px] text-gray-400 hover:text-white transition-colors"
                  >
                    Costs <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
                
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="flex items-center gap-1 text-[9px] text-white">
                    <Checkbox
                      checked={booking.isTicketPurchased}
                      onCheckedChange={(checked) => updateAccountingFlag(booking.id, 'is_ticket_purchased', checked)}
                      className="h-3 w-3 border-gray-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                    />
                    Bkd
                  </label>
                  <label className="flex items-center gap-1 text-[9px] text-white">
                    <Checkbox
                      checked={booking.isReimbursed}
                      onCheckedChange={(checked) => updateAccountingFlag(booking.id, 'is_reimbursed', checked)}
                      className="h-3 w-3 border-gray-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                    />
                    Paid
                  </label>
                  <label className="flex items-center gap-1 text-[9px] text-white">
                    <Checkbox
                      checked={booking.isDisbursed}
                      onCheckedChange={(checked) => updateAccountingFlag(booking.id, 'is_disbursed', checked)}
                      className="h-3 w-3 border-gray-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                    />
                    Rel
                  </label>
                </div>
              </div>
            </div>
            );
          })}
          {bookings.length === 0 && (
            <div className="py-8 text-center text-gray-500 text-xs">No bookings found</div>
          )}
        </div>

        {/* Mobile Cost Breakdown Sheet */}
        <Sheet open={!!costSheetBooking} onOpenChange={(open) => !open && setCostSheetBooking(null)}>
          <SheetContent side="bottom" className="bg-brand-950/95 backdrop-blur-xl border-t border-brand-800/45 rounded-t-2xl">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-white text-left">
                {costSheetBooking?.route} - Cost Breakdown
              </SheetTitle>
            </SheetHeader>
            
            {costSheetBooking && (
              <div className="space-y-4 pb-6">
                {/* Revenue Section */}
                <div className="space-y-2">
                  <h4 className="text-xs font-normal text-gray-400">Revenue</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {(costSheetBooking.paymentMethod === 'miles' || costSheetBooking.isMilesInstallments || costSheetBooking.isReserve) && (
                      <>
                        <div className="flex justify-between col-span-2">
                          <span className="text-gray-400">Payment:</span>
                          <span className="text-white capitalize">
                            {costSheetBooking.paymentMethod === 'miles' ? 'Miles Only' : costSheetBooking.isMilesInstallments ? 'Miles + Installments' : costSheetBooking.isReserve ? 'Reserve' : costSheetBooking.paymentMethod}
                          </span>
                        </div>
                      </>
                    )}
                    {costSheetBooking.isReserve && costSheetBooking.reserveCashPaymentCents > 0 && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-400">From Reserve:</span>
                          <span className="text-emerald-400">${formatCurrency(Math.max(0, costSheetBooking.grandTotal - (costSheetBooking.reserveCashPaymentCents / 100)))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Card Payment:</span>
                          <span className="text-white">${formatCurrency(costSheetBooking.reserveCashPaymentCents / 100)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-400">Base Fare:</span>
                      <span className="text-white">${formatCurrency(costSheetBooking.baseFare)}</span>
                    </div>
                    {costSheetBooking.creditsTotal > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Credits Applied:</span>
                        <span className="text-green-400">-${formatCurrency(costSheetBooking.creditsTotal)}</span>
                      </div>
                    )}
                    {costSheetBooking.isMilesInstallments && costSheetBooking.milesUsed > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Miles Used:</span>
                        <span className="text-brand-300">{costSheetBooking.milesUsed.toLocaleString()} miles</span>
                      </div>
                    )}
                    {costSheetBooking.isMilesInstallments && costSheetBooking.cashPortion > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Cash Portion:</span>
                        <span className="text-white">${formatCurrency(costSheetBooking.cashPortion)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-400">Service Fee {costSheetBooking.isFlightHotelPackage ? '' : (Number.isFinite(costSheetBooking.serviceFeeRate) ? `(${Math.round(costSheetBooking.serviceFeeRate * 100)}%)` : '')}:</span>
                      <span className={costSheetBooking.isFlightHotelPackage ? 'text-green-400' : 'text-white'}>
                        {costSheetBooking.isFlightHotelPackage ? 'Waived' : costSheetBooking.isMilesOnly && !(costSheetBooking.serviceFee > 0) ? '—' : `$${formatCurrency(costSheetBooking.serviceFee)}`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total (Base+Svc):</span>
                      <span className="text-white">${formatCurrency(costSheetBooking.totalWithFee)}</span>
                    </div>
                    {costSheetBooking.holdFee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Hold Fee ({costSheetBooking.holdFeeRate ? Math.round(costSheetBooking.holdFeeRate * 100) : 15}%):</span>
                        <span className="text-white">${formatCurrency(costSheetBooking.holdFee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between col-span-2 pt-1 border-t border-gray-800">
                      <span className="text-white font-medium">Grand Total:</span>
                      <span className="text-white font-medium">${formatCurrency(costSheetBooking.grandTotal)}</span>
                    </div>
                    {!costSheetBooking.isMilesOnly && costSheetBooking.milesPaid > 0 && (
                      // Price paid with Defera Miles, on top of the plan's total.
                      <div className="flex justify-between col-span-2">
                        <span className="text-gray-400">+ Paid with Defera Miles:</span>
                        <span className="text-white">${formatCurrency(costSheetBooking.milesPaid)}</span>
                      </div>
                    )}
                    {costSheetBooking.insurancePremium > 0 && (
                      // Pass-through: collected for Faye, outside every figure on this sheet.
                      <div className="flex justify-between col-span-2">
                        <span className="text-gray-400">+ Faye travel insurance (pass-through):</span>
                        <span className="text-white">${formatCurrency(costSheetBooking.insurancePremium)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Profit Section */}
                <div className="space-y-2">
                  <h4 className="text-xs font-normal text-gray-400">Profit Breakdown</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between col-span-2">
                      <span className="text-gray-400">Gross Profit (Service Fee):</span>
                      <span className="text-emerald-400">${formatCurrency(costSheetBooking.profit)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Costs Section */}
                <div className="space-y-2">
                  <h4 className="text-xs font-normal text-gray-400">Costs (Auto-calculated)</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Investor:</span>
                      <span className="text-red-400">-${formatCurrency(costSheetBooking.investorCost)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Miles Liability:</span>
                      <span className="text-red-400">-${formatCurrency(costSheetBooking.mileageLiability)}</span>
                    </div>
                    {(costSheetBooking.amountRefunded ?? 0) > 0 && (
                      <div className="flex justify-between col-span-2">
                        <span className="text-gray-400">Amount Refunded:</span>
                        <span className="text-red-400">-${formatCurrency(costSheetBooking.amountRefunded)}</span>
                      </div>
                    )}
                    <div className="flex justify-between col-span-2">
                      <span className="text-gray-400 text-xs">({formatCurrency(costSheetBooking.milesEarned)} miles earned)</span>
                    </div>
                  </div>
                </div>
                
                {/* Net Profit */}
                <div className="pt-2 border-t border-gray-700">
                  <div className="flex justify-between text-base font-medium">
                    <span className="text-white">Net Profit:</span>
                    <span className={costSheetBooking.netProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                      ${formatCurrency(costSheetBooking.netProfit)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Profit Modal - Owner draws by month */}
        <Sheet open={showProfitModal} onOpenChange={setShowProfitModal}>
          <SheetContent side="right" className="bg-brand-950/95 backdrop-blur-xl border-l border-brand-800/45 w-full max-w-md sm:max-w-lg overflow-y-auto">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-white text-left">Profit by Month</SheetTitle>
              <p className="text-xs text-gray-500 font-normal">Owner draws overview</p>
            </SheetHeader>
            <div className="space-y-6 pb-8">
              {profitByMonth.map(({ key, monthName, items, totalBaseFare, totalServiceFee, totalHoldFee, totalInvestorCost, totalMileageLiability, totalProfit, totalNetProfit }) => (
                <div key={key} className="space-y-2">
                  <h3 className="text-sm font-medium text-white border-b border-gray-700 pb-1.5">{monthName}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px] min-w-[400px]">
                      <thead>
                        <tr className="text-gray-500 border-b border-gray-800">
                          <th className="text-left py-1.5 px-1 w-6">#</th>
                          <th className="text-right py-1.5 px-1">Base</th>
                          <th className="text-right py-1.5 px-1">Svc</th>
                          <th className="text-right py-1.5 px-1">Hold</th>
                          <th className="text-right py-1.5 px-1">Profit</th>
                          <th className="text-right py-1.5 px-1">Inv</th>
                          <th className="text-right py-1.5 px-1">Miles</th>
                          <th className="text-right py-1.5 px-1">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((b, idx) => (
                          <tr key={b.id} className="border-b border-gray-800/50">
                            <td className="py-1 px-1 text-gray-400">{idx + 1}</td>
                            <td className="py-1 px-1 text-right text-white tabular-nums">${formatCurrency(b.baseFare)}</td>
                            <td className="py-1 px-1 text-right text-white tabular-nums">${formatCurrency(b.serviceFee ?? 0)}</td>
                            <td className="py-1 px-1 text-right text-white tabular-nums">${formatCurrency(b.holdFee)}</td>
                            <td className="py-1 px-1 text-right text-emerald-400 tabular-nums">${formatCurrency(b.profit ?? 0)}</td>
                            <td className="py-1 px-1 text-right text-red-400/90 tabular-nums">${formatCurrency(b.investorCost ?? 0)}</td>
                            <td className="py-1 px-1 text-right text-red-400/90 tabular-nums">${formatCurrency(b.mileageLiability ?? 0)}</td>
                            <td className={`py-1 px-1 text-right tabular-nums font-medium ${(b.netProfit ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>${formatCurrency(b.netProfit ?? 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="text-gray-200 border-t-2 border-gray-700 font-medium">
                          <td className="py-1.5 px-1">Total</td>
                          <td className="py-1.5 px-1 text-right tabular-nums">${formatCurrency(totalBaseFare)}</td>
                          <td className="py-1.5 px-1 text-right tabular-nums">${formatCurrency(totalServiceFee)}</td>
                          <td className="py-1.5 px-1 text-right tabular-nums">${formatCurrency(totalHoldFee)}</td>
                          <td className="py-1.5 px-1 text-right tabular-nums">${formatCurrency(totalProfit)}</td>
                          <td className="py-1.5 px-1 text-right tabular-nums">${formatCurrency(totalInvestorCost)}</td>
                          <td className="py-1.5 px-1 text-right tabular-nums">${formatCurrency(totalMileageLiability)}</td>
                          <td className={`py-1.5 px-1 text-right tabular-nums ${totalNetProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>${formatCurrency(totalNetProfit)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              ))}
              {profitByMonth.length === 0 && (
                <p className="text-gray-500 text-sm">No trips to display</p>
              )}
            </div>
          </SheetContent>
        </Sheet>

        <AdminAllTripsModal open={showAllReserveTripsModal} onClose={() => setShowAllReserveTripsModal(false)} />
        <AdminAllZeroTripsModal open={showAllZeroTripsModal} onClose={() => setShowAllZeroTripsModal(false)} />

        {/* Defera Capital — installment plans for a single upcoming due-date bucket */}
        <Sheet open={!!selectedUpcomingBucket} onOpenChange={(open) => !open && setSelectedUpcomingBucket(null)}>
          <SheetContent side="right" className="bg-brand-950/95 backdrop-blur-xl border-l border-brand-800/45 w-full max-w-md sm:max-w-lg overflow-y-auto">
            <SheetHeader className="pb-4">
              <SheetTitle className="text-white text-left">
                Upcoming Defera Capital Payments
              </SheetTitle>
              {selectedUpcomingBucket && (
                <div className="text-left">
                  <p className="text-xs text-gray-500 font-normal">
                    Due {new Date(`${selectedUpcomingBucket.date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                  <p className="mt-1 text-base font-semibold text-brand-300 tabular-nums">
                    ${formatCurrency(selectedUpcomingBucket.total_cents / 100)}
                    <span className="ml-2 text-[11px] text-gray-500 font-normal">
                      across {selectedUpcomingBucket.count} plan{selectedUpcomingBucket.count === 1 ? '' : 's'}
                    </span>
                  </p>
                  <p className="mt-0.5 text-[11px] font-normal">
                    <span className={selectedUpcomingBucket.profit_cents < 0 ? 'text-red-400/90 tabular-nums' : 'text-emerald-400/90 tabular-nums'}>
                      {selectedUpcomingBucket.profit_cents < 0 ? '−' : ''}${formatCurrency(Math.abs(selectedUpcomingBucket.profit_cents) / 100)}
                    </span>
                    <span className="text-gray-500"> profit in this day&apos;s collections</span>
                  </p>
                </div>
              )}
            </SheetHeader>
            {selectedUpcomingBucket && (
              <div className="space-y-2 pb-8">
                {selectedUpcomingBucket.items.map((charge) => {
                  // Buckets carry only {booking_id, amount_cents, projected}; the
                  // plan itself lives once in the `plans` map the endpoint returns.
                  const item = upcomingCapitalPlans[charge.booking_id];
                  if (!item) return null;
                  const intervalLabel = item.interval
                    ? `${item.interval_count > 1 ? item.interval_count + ' ' : ''}${item.interval}${(item.interval_count || 1) > 1 ? 's' : ''}`
                    : null;
                  return (
                    <button
                      key={charge.booking_id}
                      type="button"
                      onClick={() => {
                        sessionStorage.setItem('open_booking_id', charge.booking_id);
                        navigate('/trips');
                      }}
                      className="w-full text-left rounded-lg border border-gray-800/60 bg-white/[0.02] hover:bg-white/[0.04] p-3 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-white truncate" title={item.user_email}>
                            {item.user_name || item.user_email || 'Unknown'}
                          </p>
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            {item.route}
                            {item.departure_date && (
                              <span className="text-gray-500"> · departs {new Date(item.departure_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            )}
                          </p>
                          <p className="text-[10px] text-gray-500 font-mono mt-0.5">
                            {item.booking_reference || item.booking_id?.slice(0, 8)}
                            {intervalLabel && <span className="ml-2 text-gray-500">· every {intervalLabel}</span>}
                            <span className="ml-2 px-1 py-0.5 rounded text-[8px] uppercase tracking-wider bg-white/5 text-gray-400">
                              {(item.payment_processor || 'stripe') === 'nmi' ? 'NMI' : 'Stripe'}
                            </span>
                            {item.subscription_status && item.subscription_status !== 'active' && (
                              <span className="ml-2 px-1 py-0.5 rounded text-[8px] uppercase tracking-wider bg-brand-500/20 text-brand-400">
                                {item.subscription_status}
                              </span>
                            )}
                            {item.deposit_expected_cents > 0 && item.deposit_proven === false && (
                              <span
                                className="ml-2 px-1 py-0.5 rounded text-[8px] uppercase tracking-wider bg-amber-500/15 text-amber-300/90"
                                title={`Plan expects a $${formatCurrency(item.deposit_expected_cents / 100)} deposit but no succeeded Stripe charge backs it — excluded from paid.`}
                              >
                                deposit unproven
                              </span>
                            )}
                            {charge.projected && (
                              <span
                                className="ml-2 px-1 py-0.5 rounded text-[8px] uppercase tracking-wider bg-white/5 text-gray-500"
                                title="Stripe stores only the next renewal date. This charge is the cadence walked forward against the remaining balance."
                              >
                                projected
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-medium text-brand-300 tabular-nums">
                            ${formatCurrency(charge.amount_cents / 100)}
                          </p>
                          <p
                            className={`text-[10px] mt-0.5 tabular-nums ${charge.profit_cents < 0 ? 'text-red-400/90' : 'text-emerald-400/90'}`}
                            title={item.margin_basis === 'actual_cost'
                              ? `Trip margin $${formatCurrency(item.margin_cents / 100)} — trip total minus the $${formatCurrency((item.actual_cost_cents || 0) / 100)} we paid the airline. This charge carries its pro-rata share.`
                              : `Service fee only — no actual airline cost on file, so the $${formatCurrency(item.hold_fee_cents / 100)} hold fee is not counted as profit yet.`}
                          >
                            {charge.profit_cents < 0 ? '−' : ''}${formatCurrency(Math.abs(charge.profit_cents) / 100)} profit
                          </p>
                          {item.total_amount_cents != null && (
                            <p className="text-[10px] text-gray-500 mt-0.5">
                              of ${formatCurrency(item.total_amount_cents / 100)} total
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
                <p className="pt-3 text-[10px] text-gray-400">
                  Live amounts — NMI rows are the schedule the daily cron charges; Stripe past its next
                  renewal is the cadence projected against the remaining balance. Click a plan to open the trip.
                </p>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
